import { useMemo, useState } from "react";
import { IoClose } from "react-icons/io5";
import { toast } from "react-toastify";
import { Campaign, CampaignRecipient } from "../../../features/campaign/campaignTypes";
import { giftTypeOptions } from "../../../features/emailTemplate/emailTypes";
import { useGetPriceListQuery } from "../../../features/users/usersApi";
import {
  AutomatedGiftTab,
  useRemoveAutomatedRecipientMutation,
  useSetAutomatedGiftStatusMutation,
  useUpdateAutomatedRecipientMutation,
} from "../../../features/automatedGifts/automatedGiftsApi";
import DeleteConfirmation from "../../../components/modal/DeleteConfirmation";
import {
  activeRecipients,
  formatEventMmDd,
  groupRecipientsByMonth,
  isRecipientActive,
  priceOptionsForGiftType,
  resolveDefaultPriceId,
  visibleRecipients,
} from "../utils";
import AddRecipientDrawer from "./AddRecipientModal";
import AgSelect from "./AgSelect";
import CampaignSettings from "./CampaignSettings";

const giftTypeSelectOptions = giftTypeOptions.map((opt) => ({
  value: opt.value,
  label: opt.label,
}));

interface CampaignPanelProps {
  type: AutomatedGiftTab;
  campaign?: Campaign;
  title: string;
  description: string;
  emptyCta: string;
}

const CampaignPanel = ({
  type,
  campaign,
  title,
  description,
  emptyCta,
}: CampaignPanelProps) => {
  const [showAdd, setShowAdd] = useState(false);
  const [openMonths, setOpenMonths] = useState<Record<number, boolean>>({});
  const [removeTarget, setRemoveTarget] = useState<CampaignRecipient | null>(
    null,
  );
  const [setStatus, { isLoading: isToggling }] =
    useSetAutomatedGiftStatusMutation();
  const [removeRecipient, { isLoading: isRemoving }] =
    useRemoveAutomatedRecipientMutation();
  const [updateRecipient, { isLoading: isUpdatingRecipient }] =
    useUpdateAutomatedRecipientMutation();
  const { data: priceList } = useGetPriceListQuery(undefined);

  const recipients = useMemo(
    () => visibleRecipients(campaign?.recipient_list),
    [campaign?.recipient_list],
  );
  const activeCount = useMemo(
    () => activeRecipients(campaign?.recipient_list).length,
    [campaign?.recipient_list],
  );
  const groups = useMemo(() => groupRecipientsByMonth(recipients), [recipients]);
  const isActive = Boolean(campaign?.is_active);

  const toggleStatus = async () => {
    try {
      await setStatus({ type, is_active: !isActive }).unwrap();
      toast.success(!isActive ? "Campaign is running" : "Campaign paused");
    } catch (error: any) {
      toast.error(error?.data?.message || "Could not update status");
    }
  };

  const confirmRemove = async () => {
    const recipientId = removeTarget?.recipient_id;
    if (!recipientId) return;
    try {
      await removeRecipient({ type, recipientId }).unwrap();
      setRemoveTarget(null);
      toast.success("Recipient removed");
    } catch (error: any) {
      toast.error(error?.data?.message || "Could not remove recipient");
    }
  };

  const handleRecipientPatch = async (
    recipient: CampaignRecipient,
    patch: { gift_type?: string; price_id?: string; is_active?: boolean },
  ) => {
    if (!recipient.recipient_id) return;
    try {
      await updateRecipient({
        type,
        recipientId: recipient.recipient_id,
        data: patch,
      }).unwrap();
      if (patch.is_active !== undefined) {
        toast.success(
          patch.is_active ? "Recipient activated" : "Recipient deactivated",
        );
      }
    } catch (error: any) {
      toast.error(error?.data?.message || "Could not update recipient");
    }
  };

  return (
    <div className="ag-panel">
      <div className="ag-status-row">
        <div className="ag-status-left">
          <span className={`ag-pill ${isActive ? "on" : "off"}`}>
            {isActive ? "On" : "Off"}
          </span>
          <div className="ag-status-copy">
            {isActive ? "Your campaign is running." : "Campaign is paused."}
          </div>
        </div>
        <label className="ag-switch" title="Toggle campaign">
          <input
            type="checkbox"
            checked={isActive}
            disabled={isToggling || !campaign}
            onChange={toggleStatus}
          />
          <span />
        </label>
      </div>

      <CampaignSettings
        type={type}
        campaign={campaign}
        recipientCount={recipients.length}
      />

      {recipients.length === 0 ? (
        <div className="ag-empty">
          <div className="ag-empty-art" aria-hidden>
            <svg width="42" height="42" viewBox="0 0 48 48" fill="none">
              <circle cx="16" cy="16" r="8" fill="#e2b35a" />
              <circle cx="32" cy="14" r="7" fill="#c97b7b" />
              <circle cx="26" cy="28" r="8" fill="#6ea8a0" />
              <path
                d="M16 24v14M32 21v17M26 36v6"
                stroke="#8f6a35"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </div>
          <h2>{title}</h2>
          <p>{description}</p>
          <button
            type="button"
            className="ag-btn-primary"
            onClick={() => setShowAdd(true)}
          >
            {emptyCta} ({recipients.length})
          </button>
        </div>
      ) : (
        <>
          <div className="ag-list-head">
            <h3>
              {type === "birthday" ? "Birthday list" : "Home anniversary list"}
            </h3>
            <div className="ag-list-head-meta">
              <span className="ag-recipient-summary">
                {activeCount} active · {recipients.length} total
              </span>
              <button
                type="button"
                className="ag-btn-ghost"
                onClick={() => setShowAdd(true)}
              >
                + Add recipient(s)
              </button>
            </div>
          </div>

          {groups.map((group) => {
            const open = openMonths[group.monthIndex] ?? false;
            return (
              <div className="ag-month" key={group.monthIndex}>
                <button
                  type="button"
                  className={`ag-month-toggle${open ? " is-open" : ""}`}
                  onClick={() =>
                    setOpenMonths((prev) => ({
                      ...prev,
                      [group.monthIndex]: !open,
                    }))
                  }
                  aria-expanded={open}
                >
                  <div className="ag-month-toggle-label">
                    <span className="ag-month-name">{group.label}</span>
                    <span className="ag-month-count">
                      {group.recipients.length} recipient
                      {group.recipients.length === 1 ? "" : "s"}
                    </span>
                  </div>
                  <span className="ag-month-chevron" aria-hidden />
                </button>
                {open && (
                  <table className="ag-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Recipient</th>
                        <th>Event date</th>
                        <th>Gift type</th>
                        <th>Price</th>
                        <th>Active</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {group.recipients.map((recipient, index) => {
                        const giftType =
                          recipient.gift_type || campaign?.gift_type || "";
                        const priceOptions = priceOptionsForGiftType(
                          priceList?.data,
                          giftType,
                        );
                        const selectedPriceId = resolveDefaultPriceId(
                          priceList?.data,
                          giftType,
                          recipient.price_id,
                        );
                        const recipientActive = isRecipientActive(recipient);
                        return (
                          <tr
                            key={recipient.recipient_id || recipient.email}
                            className={recipientActive ? "" : "is-inactive"}
                          >
                            <td>{index + 1}</td>
                            <td className="ag-recipient-cell">
                              <div className="ag-recipient-identity">
                                <div className="ag-recipient-name">
                                  {recipient.name}
                                </div>
                                <div className="ag-recipient-email">
                                  {recipient.email}
                                </div>
                              </div>
                              <div className="ag-recipient-schedule">
                                <span className="ag-tag ag-tag-next">
                                  <span className="ag-tag-label">Next</span>
                                  <span className="ag-tag-value">
                                    {formatEventMmDd(recipient.trigger_date, {
                                      includeYear: true,
                                    })}
                                  </span>
                                </span>
                                {recipient.last_sent_on ? (
                                  <span className="ag-tag ag-tag-sent">
                                    <span className="ag-tag-label">Last</span>
                                    <span className="ag-tag-value">
                                      {formatEventMmDd(recipient.last_sent_on, {
                                        includeYear: true,
                                      })}
                                    </span>
                                  </span>
                                ) : null}
                              </div>
                            </td>
                            <td className="ag-date-cell">
                              {formatEventMmDd(recipient.event_date, {
                                includeYear: type === "home-anniversary",
                              })}
                            </td>
                            <td className="ag-select-cell">
                              <AgSelect
                                value={giftType}
                                options={giftTypeSelectOptions}
                                onChange={(nextType) => {
                                  handleRecipientPatch(recipient, {
                                    gift_type: nextType,
                                    price_id: resolveDefaultPriceId(
                                      priceList?.data,
                                      nextType,
                                      recipient.price_id,
                                    ),
                                  });
                                }}
                                placeholder="Gift type"
                              />
                            </td>
                            <td className="ag-select-cell">
                              <AgSelect
                                value={selectedPriceId}
                                options={priceOptions}
                                onChange={(nextPriceId) =>
                                  handleRecipientPatch(recipient, {
                                    price_id: nextPriceId,
                                  })
                                }
                                placeholder="Price"
                                isDisabled={!giftType}
                              />
                            </td>
                            <td className="ag-active-cell">
                              <label
                                className="ag-switch ag-switch-sm"
                                title={
                                  recipientActive
                                    ? "Deactivate recipient"
                                    : "Activate recipient"
                                }
                              >
                                <input
                                  type="checkbox"
                                  checked={recipientActive}
                                  disabled={
                                    isUpdatingRecipient || !recipient.recipient_id
                                  }
                                  onChange={() =>
                                    void handleRecipientPatch(recipient, {
                                      is_active: !recipientActive,
                                    })
                                  }
                                />
                                <span />
                              </label>
                            </td>
                            <td className="ag-remove-cell">
                              <button
                                type="button"
                                className="ag-remove"
                                aria-label={`Remove ${recipient.name || recipient.email}`}
                                title="Remove"
                                onClick={() => setRemoveTarget(recipient)}
                              >
                                <IoClose size={18} />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            );
          })}
        </>
      )}

      <AddRecipientDrawer
        show={showAdd}
        type={type}
        campaign={campaign}
        onHide={() => setShowAdd(false)}
      />

      <DeleteConfirmation
        show={Boolean(removeTarget)}
        onHide={() => setRemoveTarget(null)}
        onConfirmation={() => {
          if (!isRemoving) void confirmRemove();
        }}
        title="Remove recipient?"
        description={
          removeTarget
            ? `Are you sure you want to remove ${removeTarget.name || removeTarget.email} from this campaign?`
            : "Are you sure you want to remove this recipient?"
        }
      />
    </div>
  );
};

export default CampaignPanel;
